import { useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { useSites } from 'store'
import { deserializeErrors } from 'apollo-client'

import { modelTypeToMarkerProps } from '../Supervision/helpers'
import { Map, Device } from './types'


const sortFloors = (floors: Map[]) => {
  return [...floors].sort((a, b) => {
    const levelA = parseInt(a.level)
    const levelB = parseInt(b.level)
    return (!isNaN(levelA) ? levelA : 0) - (!isNaN(levelB) ? levelB : 0)
  })
}

export const OPERATION_SITE = gql`
  query CartographyAPsZones($sf_id: ID!) {
    surface_areas: cartographyAPsZones(sf_id: $sf_id) {
      name
      uuid
      maps {
        uuid
        name
        level
        devices {
          uuid
          label
          status
          connected_stations
          radio_enabled
          mac
          position {
            x
            y
          }
        }
      }
    }
  } 
`

export const deviceToMarker = (device: Device) => {
  return {
    ...device,
    markers: [{
      id: device.uuid,
      name: device.label,
      type: 'ap',
      superscript: device.connected_stations,
      ...modelTypeToMarkerProps({
        type: 'ap',
        available_level: 'radio_enabled',
      }),
      selected: false
    }]
  }
}

const dataToTree = ({data, noDeviceName}) => {
  return data.map(surfaceArea => {
    const maps = sortFloors(surfaceArea.maps)
    return {
      id: surfaceArea.uuid,
      internalId: surfaceArea.uuid,
      name: surfaceArea.name,
      data: { ...surfaceArea, type: 'surface_area', },
      children: maps.map(map => {
        const deviceChildren = map.devices
          .filter(device => device.status !== 'spare')
          .map(device => ({
            id: device.uuid,
            internalId: device.uuid,
            name: device.label,
            data: { ...device, type: 'device' as const },
            children: []
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
        const children = deviceChildren.length > 0
          ? deviceChildren
          : [{
            id: `empty-${map.uuid}`,
            internalId: `empty-${map.uuid}`,
            name: noDeviceName,
            data: { uuid: `empty-${map.uuid}`, label: ('access_point_zones.no_device'), type: 'empty' as const },
            children: []
          }]

        return {
          id: map.uuid,
          internalId: map.uuid,
          name: map.name,
          data: { ...map, type: 'map' as const },
          children
        }
      })
    }
  })
}

const useSurfaceArea = () => {
  const [{selected}] = useSites()
  const {data, loading, error} = useQuery(OPERATION_SITE, {
    fetchPolicy: 'network-only',
    variables: {
      sf_id: selected[0]?.sf_id
    },
    skip: selected.length !== 1
  })
  return { data: data?.surface_areas || [], loading, error }
}

export const useTree = () => {
  const {data, loading, error} = useSurfaceArea()
  const [{selected}] = useSites()
  const { t } = useTranslation()
  const tree = useMemo(() => {
    if (!data) {
      return []
    }
    const tree = dataToTree({ data, noDeviceName: t('access_point_zones.no_device') })
    if (tree.length > 1) {
      return [{
        id: selected[0].sf_id,
        internalId: selected[0].sf_id,
        name: selected[0].name,
        data: { ...selected[0], type: 'site' },
        children: tree

      }]
    }

    return tree
  }, [data, t])

  return {
    tree,
    loading,
    // @ts-expect-error deserializeErrors is defined in JSX without type definitions
    error: error ? deserializeErrors(error) : null,
  }
}